import { useMemo } from 'react';

import { Heading, Skeleton, Stack, Table, Tbody } from '@ecoinvent/ui';
import { useParams } from 'react-router';

import AppCard from 'components/UI/AppCard';
import NoResultCard from 'components/UI/NoResultCard';
import { v4DocumentationResponse } from 'hooks/data/datasets/V4/documentation/types';
import useV4DatasetDocumentation from 'hooks/data/datasets/V4/documentation/useV4DatasetDocumentation';
import useV4Dataset from 'hooks/data/datasets/V4/useV4Dataset';

import DocumentationItem from '../components/DocumentationItem';
import { buildV4DocumentationConfig, loadingConfig } from '../documentationConfig';
import V4KeyData from '../v4KeyData';

type Props = {
  isLoading?: boolean;
};

const V4Documentation = ({ isLoading }: Props) => {
  const { spold_id } = useParams();
  const { data: v4KeyData } = useV4Dataset(spold_id);
  const { data: documentationDataV4, isLoading: isV4DocumentationLoadingV4 } = useV4DatasetDocumentation(spold_id);

  const documentationConfig = useMemo(
    () => (documentationDataV4 ? buildV4DocumentationConfig(documentationDataV4 as v4DocumentationResponse) : []),
    [documentationDataV4]
  );

  const isAnythingLoading = isLoading || isV4DocumentationLoadingV4;

  const configToRender = isAnythingLoading ? loadingConfig : documentationConfig;

  return (
    <Stack>
      {!documentationDataV4?.general_comment ? (
        <Table>
          <Tbody>
            <NoResultCard>This dataset doesn't seem to have documentation.</NoResultCard>
          </Tbody>
        </Table>
      ) : (
        <Stack width={'100%'} spacing={5} alignItems={'flex-start'} direction={{ base: 'column-reverse', xl: 'row' }}>
          <AppCard width={'100%'} alignSelf={'stretch'}>
            <Stack spacing={8} width={'100%'}>
              <Stack>
                <Skeleton isLoaded={!isAnythingLoading}>
                  <Heading size="md">Documentation</Heading>
                </Skeleton>
              </Stack>
              {configToRender.map(({ label, value, renderValue }) => (
                <DocumentationItem
                  isLoading={isAnythingLoading}
                  key={label}
                  label={label}
                  value={value}
                  renderValue={renderValue}
                />
              ))}
            </Stack>
          </AppCard>
          <Stack
            maxW={{ base: '100%', xl: '350px' }}
            w="100%"
            position={{ base: 'relative', xl: 'sticky' }}
            top={{ base: 'auto', xl: 4 }}
          >
            <V4KeyData
              isLoading={isAnythingLoading}
              geography={v4KeyData?.geography}
              geography_short_name={v4KeyData?.geography_short}
              time_period={documentationDataV4?.time_period}
              reference_product={v4KeyData?.product}
              unit={v4KeyData?.unit}
              activity_type={documentationDataV4?.type}
            />
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

export default V4Documentation;
